import { isEqual, isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useToast } from '@shared/components/toast';
import { BuilderComponentOpenProvider } from '@shared/scenes/workflows/edit/context';
import { useChannel } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator, useUnsavedChangesNavigationWarning } from '@shared/util/navigation';
import { WorkflowTemplateDataProvider } from '../context/WorkflowTemplateDataProvider';
import { WorkflowTemplateProvider } from '../context/context';
import { MBWorkflowEditComponent } from '../edit/components';
import { useCreateWorkflowTemplate, useUpdateSubformComponentWorkflows } from '../hooks';
import { WorkspaceDetail } from '../hooks/useWorkspaceDetails';
import { initialWorkflowTemplate } from './hooks';

interface Props {
  channelSlug: string;
  onSubmitFormButtonCallback?(fn: () => void): void;
  onDirty?(dirty: boolean): void;
  name: string;
  workspaceDetails: Map<string, WorkspaceDetail>;
  goBackOverride?: () => void;
  workflowTemplateId: string;
  subformComponentId?: string;
}

export function MBWorkflowCreate({
  channelSlug,
  onDirty,
  onSubmitFormButtonCallback,
  name,
  workflowTemplateId,
  subformComponentId,
  workspaceDetails,
  goBackOverride,
}: Props) {
  const [workflowTemplate, setWorkflowTemplate] = useState({
    ...initialWorkflowTemplate,
    name,
  });
  const submitting = useRef(false);
  const newSubformSaved = useRef(false);
  const toast = useToast();
  const { goBack } = useNavigator();
  const channel = useChannel({ channelSlug });
  const [createWorkflow, { loading: mutationLoading }] = useCreateWorkflowTemplate(true);
  const updateSubformComponentWorkflows = useUpdateSubformComponentWorkflows(
    workflowTemplateId,
    subformComponentId,
  );

  const onBack = useMemo(() => {
    return isNil(goBackOverride) ? goBack : goBackOverride;
  }, [goBack, goBackOverride]);

  const onSubmit = useCallback(async () => {
    submitting.current = true;
    try {
      const newWorkflowTemplate = await createWorkflow(workflowTemplate);
      if (
        !isNil(newWorkflowTemplate?.id) &&
        subformComponentWorkflowsNeedUpdate(workflowTemplateId, subformComponentId)
      ) {
        await updateSubformComponentWorkflows(newWorkflowTemplate.id);
      }
      newSubformSaved.current = true;
      submitting.current = false;
      toast.show({
        icon: 'check-filled',
        message: i18n('workflowTemplate.successfulUpdate'),
      });
      onBack();
    } catch (e) {
      console.error(e);
      submitting.current = false;
      toast.show({
        style: 'warning',
        message: i18n('workflowTemplate.errorUpdate'),
      });
    }
  }, [
    createWorkflow,
    workflowTemplate,
    workflowTemplateId,
    subformComponentId,
    toast,
    onBack,
    updateSubformComponentWorkflows,
  ]);

  const isDirty = useMemo(
    () => !isEqual(workflowTemplate, initialWorkflowTemplate),
    [workflowTemplate],
  );

  useUnsavedChangesNavigationWarning(!newSubformSaved.current && !submitting.current);

  useEffect(() => {
    onSubmitFormButtonCallback?.(onSubmit);
  }, [onSubmit, onSubmitFormButtonCallback]);

  useEffect(() => {
    onDirty?.(isDirty);
  }, [isDirty, onDirty]);

  return (
    <CardChannelContext.Provider value={channel}>
      <BuilderComponentOpenProvider>
        <WorkflowTemplateDataProvider channelId={channel?.id}>
          <WorkflowTemplateProvider
            initialWorkflowTemplate={workflowTemplate}
            onUpdate={setWorkflowTemplate}
          >
            <MBWorkflowEditComponent
              workflowTemplateId={workflowTemplateId}
              loading={mutationLoading}
              workspaceDetails={workspaceDetails}
              isSubform
              createMode
            />
          </WorkflowTemplateProvider>
        </WorkflowTemplateDataProvider>
      </BuilderComponentOpenProvider>
    </CardChannelContext.Provider>
  );
}

function subformComponentWorkflowsNeedUpdate(
  workflowTemplateId?: string,
  subformComponentId?: string,
): boolean {
  return !isNil(workflowTemplateId) && !isNil(subformComponentId);
}
